/**
 * FeaturedProductCard.jsx
 * Price display removed — all enquiries via WhatsApp.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, MessageCircle, Eye } from 'lucide-react';
import { openWhatsAppInquiry } from '../utils/inquiry';
import { normalizeImages } from '../utils/imageUtils'; // ✅ HEIC fix

const FeaturedProductCard = ({ product, index, onAddToCart }) => {
  const navigate = useNavigate();
  const [imgIdx, setImgIdx] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  // ✅ normalizeImages converts HEIC URLs → f_auto (JPEG/WebP) for all browsers
  const images = normalizeImages(product.images);

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    navigate(`/product/${product.sku}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAddToCart(product);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    openWhatsAppInquiry({
      productSku: product.sku,
      productCategory: product.category,
      productTitle: product.title,
    });
  };

  return (
    <div
      onClick={handleCardClick}
      data-testid={`featured-product-${index}`}
      className="featured-product-card group flex-shrink-0 w-64 cursor-pointer"
      style={{ userSelect: 'none' }}
    >
      {/* ── Image Area ── */}
      <div className="relative overflow-hidden bg-[#F7F2EE]" style={{ height: '320px' }}>
        <img
          src={images[imgIdx]}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg'; }}
        />

        {/* ✅ Hover second image — also normalised */}
        {images.length > 1 && (
          <img
            src={images[1]}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}

        {/* Category badge */}
        <div className="absolute top-3 left-3">
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-sm"
            style={{ background: 'rgba(42,0,0,0.75)', color: '#E5A93C', letterSpacing: '0.12em' }}>
            {product.category}
          </span>
        </div>

        {/* Hover action buttons */}
        <div
          className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out flex gap-1.5 p-3"
          style={{ background: 'linear-gradient(to top, rgba(42,0,0,0.92), rgba(42,0,0,0.6), transparent)' }}
        >
          <button
            onClick={handleWhatsApp}
            data-testid={`whatsapp-inquiry-button-${index}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-sm text-xs font-semibold transition-all duration-150 hover:scale-[1.03] active:scale-95"
            style={{ background: '#25D366', color: '#fff' }}
          >
            <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Inquire</span>
          </button>

          <button
            onClick={handleAddToCart}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-sm text-xs font-semibold transition-all duration-150 hover:scale-[1.03] active:scale-95"
            style={{
              background: addedToCart ? '#E5A93C' : 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(229,169,60,0.6)',
              color: addedToCart ? '#2A0000' : '#fff',
              backdropFilter: 'blur(4px)',
            }}
          >
            <ShoppingBag className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{addedToCart ? 'Added!' : 'Cart'}</span>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.sku}`); }}
            aria-label="Quick view"
            className="w-9 flex items-center justify-center rounded-sm transition-all duration-150 hover:scale-[1.03] active:scale-95"
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
        </div>

        {images.length > 1 && (
          <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setImgIdx(i); }}
                className="w-1.5 h-1.5 rounded-full transition-all"
                style={{ background: i === imgIdx ? '#E5A93C' : 'rgba(255,255,255,0.5)' }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Info Area ── */}
      <div className="pt-3 pb-4 px-0.5">
        <p className="text-[10px] font-mono tracking-widest text-gray-400 mb-0.5 uppercase">{product.sku}</p>
        <h3 className="font-semibold text-[#2C2C2C] text-sm leading-tight mb-1 line-clamp-2 group-hover:text-[#2A0000] transition-colors">
          {product.title}
        </h3>
        {product.color && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full border border-gray-200"
              style={{ background: product.color.toLowerCase().replace(/\s+/g, '') }} />
            {product.color}
          </p>
        )}
      </div>
    </div>
  );
};

export default FeaturedProductCard;